
import React from 'react';
import Heading from '@/components/ui/heading';
import { ArrowRight, Search, Sparkles, FileText } from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    {
      icon: Search,
      title: "Enter any company or market topic",
      description: "Simply type in a company name, industry, or specific market question you want to research."
    },
    {
      icon: Sparkles,
      title: "Our AI analyzes thousands of data sources",
      description: "Our advanced algorithms scan multiple sources, with the power of deep-research to generate reports in real-time."
    },
    {
      icon: FileText,
      title: "Receive a comprehensive, actionable report",
      description: "Get a detailed report with market trends, competitive analysis, and actionable recommendations."
    }
  ];

  return (
    <section className="py-28 bg-gradient-to-b from-gray-800 to-gray-900 relative overflow-hidden">
      {/* Enhanced background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/10 via-gray-900 to-gray-900"></div>
      <div className="absolute top-0 w-full h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent"></div>
      
      <div className="container relative z-10 mx-auto px-4">
        <div className="text-center mb-20">
          <span className="inline-block py-1 px-3 text-sm font-medium bg-blue-900/40 text-blue-300 rounded-full backdrop-blur-sm border border-blue-800/50 mb-3">Simple Process</span>
          <Heading level={2} className="mb-6">
            How It Works
          </Heading>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            Our powerful AI transforms complex market data into clear, actionable insights in just three simple steps.
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-6 lg:gap-10 max-w-5xl mx-auto">
          {steps.map((step, index) => {
            const Icon = step.icon;
            
            return (
              <div key={index} className="flex-1 relative group">
                {/* Step content with enhanced hover effect */}
                <div className="p-8 h-full rounded-xl border border-gray-700/30 bg-gray-800/20 backdrop-blur-sm flex flex-col items-center text-center transition-all duration-500 hover:bg-gray-800/40 hover:border-blue-700/30 hover:shadow-xl hover:shadow-blue-900/5 group-hover:transform group-hover:translate-y-[-5px]">
                  {/* Circle around the icon */}
                  <div className="mb-8 p-6 rounded-full bg-gradient-to-br from-gray-800/80 to-gray-900/80 border border-gray-700/30 group-hover:scale-110 transition-transform duration-300">
                    <Icon size={36} className="text-blue-400" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-semibold text-blue-300 mb-4 group-hover:text-blue-200 transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {step.description}
                  </p>
                </div>
                
                {/* Arrow connector (not on the last item) */}
                {index < steps.length - 1 && (
                  <div className="hidden md:flex absolute top-1/2 -right-5 lg:-right-7 transform -translate-y-1/2 items-center justify-center text-blue-500/70 group-hover:text-blue-400/90 transition-colors">
                    <ArrowRight className="w-5 h-5 lg:w-6 lg:h-6" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
